import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import {
  finishReservation,
  finishWalkIn,
} from 'restaurantAdmin/reservations/apiHelpers';
import { Button, ButtonVariants } from '@components/button/Button';
import { Modal } from '@components/modal/Modal';
import { ModalActions } from '@components/modal/ModalActions';
import { errorToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import { useMutation } from '@tanstack/react-query';
import { capitalize } from 'lodash-es';
import { isReservation, ServiceOccupant } from './apiHelpers';

const useFinishOccupant = ({ onSuccess }: { onSuccess: () => void }) => {
  const { id: restaurantId } = useRestaurant();

  return useMutation({
    mutationFn: async (occupant: Pick<ServiceOccupant, 'id' | 'type'>) => {
      if (isReservation(occupant)) {
        await finishReservation({
          restaurantId,
          reservationId: occupant.id,
        });
      } else {
        await finishWalkIn({
          restaurantId,
          walkInId: occupant.id,
        });
      }
    },
    onSuccess,
    onError: (error) => {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      } else {
        errorToast({
          message: `Failed to finish guest. Please try again.`,
        });
      }
      reportAppError(error);
    },
  });
};

export interface OccupantFinishedConfirmationModalProps {
  isOpen: boolean;
  closeModal: () => void;
  occupant: Pick<ServiceOccupant, 'id' | 'type'>;
}

export const OccupantFinishedConfirmationModal = ({
  isOpen,
  closeModal,
  occupant,
}: OccupantFinishedConfirmationModalProps) => {
  const finishOccupantMutation = useFinishOccupant({ onSuccess: closeModal });

  const occupantType = occupant.type === 'walkIn' ? 'walk-in' : 'guest';

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      title={`${capitalize(occupantType)} Finished`}
      subtitle={`Are you sure you would like to finish the ${occupantType}?`}
    >
      <ModalActions>
        <Button
          label="Cancel"
          onClick={closeModal}
          variant={ButtonVariants.Tertiary}
        />
        <Button
          label={`Yes, ${occupantType} finished`}
          onClick={() => finishOccupantMutation.mutate(occupant)}
          variant={ButtonVariants.Primary}
        />
      </ModalActions>
    </Modal>
  );
};
